import { Form, Switch } from "antd";
import { useForm } from "antd/es/form/Form";
import LoadingSpin from "components/Loading spins/LoadingSpin";
import { Notification } from "constant/notifications";
import AntDInputField from "pages/editProfile/AntDInputField";
import { useEffect, useRef, useState } from "react";
import { RiImageAddLine } from "react-icons/ri";
import { toast } from "react-toastify";
import PlaylistService from "services/music/PlaylistService";
import CreatePlaylist from "types/CreatePlaylist";

const onFinishFailed = (errorInfo: any) => {
  toast.error(errorInfo.errorFields[0].errors[0]);
};

const CreatePlayList = ({
  closeModal,
  setUserPlaylists,
  currentPlaylist,
  setPlaylist,
}: any) => {
  const [form] = useForm<CreatePlaylist>();
  const [loadingSpin, setLoadingSpin] = useState<boolean>(false);
  const [makePublic, setMakePublic] = useState<boolean>(
    currentPlaylist?.visibility === 1 ? true : false
  );
  const [playlistImgeDetails, setPlaylistImageDetails] = useState("");
  const [profileImge, setProfileImage] = useState("");
  const upload = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const initialValue: CreatePlaylist = {
      playlistName: currentPlaylist?.title,
      description: currentPlaylist?.description
        ? currentPlaylist?.description
        : "",
      artwork: currentPlaylist?.artwork_url ? currentPlaylist?.artwork_url : "",
      visibility: currentPlaylist?.visibility,
    };
    form.setFieldsValue(initialValue);
  }, [currentPlaylist, form]);

  // upload image
  const handleFileChange = (event: any) => {
    const fileObj = event.target.files && event.target.files[0];
    if (!fileObj) {
      return;
    } else {
      if (fileObj.type.startsWith("image/")) {
        setPlaylistImageDetails(fileObj);
        setProfileImage(URL.createObjectURL(fileObj));
      } else {
        toast.error("Please select a valid image file.");
      }
    }
    event.target.value = null;
  };

  const onChange = (checked: boolean) => {
    setMakePublic(checked);
  };

  const createNewPlaylist = async (formDataObj: any) => {
    await PlaylistService.createPlaylist(formDataObj)
      .then((response: any) => {
        if (response.status === 200) {
          toast.success(Notification.PlaylistCreated);
          PlaylistService.getUserPlaylists().then((res) => {
            setUserPlaylists(res.data.data);
            setLoadingSpin(false);
            closeModal();
          });
        } else {
          toast.error(Notification.PlaylistError);
        }
      })
      .catch((e: any) => {
        setLoadingSpin(false);
        toast.error("An error occurred. Please try again.");
      });
  };

  const UpdatePlaylist = async (formDataObj: any) => {
    await PlaylistService.updatePlaylist(formDataObj)
      .then((response: any) => {
        if (response.status === 200) {
          toast.success(Notification.PlaylistUpdated);
          PlaylistService.getPlaylistByPlaylistId(currentPlaylist?.id).then(
            (res) => {
              setPlaylist(res.data);
              setLoadingSpin(false);
              closeModal();
            }
          );
          PlaylistService.getUserPlaylists().then((res) => {
            setUserPlaylists(res.data.data);
          });
        } else {
          toast.error(Notification.PlaylistError);
        }
      })
      .catch((e: any) => {
        setLoadingSpin(false);
        toast.error(Notification.PlaylistError);
      });
  };

  const onFinish = async (value: CreatePlaylist) => {
    setLoadingSpin(true);

    if (currentPlaylist) {
      const finalObj: any = {
        ...value,
        id: currentPlaylist?.id,
        artwork: playlistImgeDetails
          ? playlistImgeDetails
          : currentPlaylist?.artwork_url,
        visibility: makePublic ? 1 : 0,
      };
      const formDataObj = new FormData();
      for (const key in finalObj) {
        formDataObj.append(key, finalObj[key]);
      }
      UpdatePlaylist(formDataObj);
    } else {
      const finalObj: any = {
        ...value,
        artwork: playlistImgeDetails,
        visibility: makePublic ? 1 : 0,
      };
      const formDataObj = new FormData();
      for (const key in finalObj) {
        formDataObj.append(key, finalObj[key]);
      }
      createNewPlaylist(formDataObj);
    }
  };

  // preview: newly picked image > existing artwork > placeholder
  const previewArtwork = profileImge || currentPlaylist?.artwork_url || "";

  return (
    <div>
      <h1 className="text-xl font-bold text-white">
        {currentPlaylist ? `Update Playlist` : `Create Playlist`}
      </h1>
      <hr className="my-4 border-t border-white/10" />

      <Form
        form={form}
        onFinishFailed={onFinishFailed}
        onFinish={onFinish}
        autoComplete="on"
      >
        <div className="flex gap-6 max-sm:flex-col max-sm:items-center">
          {/* artwork uploader */}
          <div className="shrink-0">
            <label className="group relative flex h-[180px] w-[180px] cursor-pointer items-center justify-center overflow-hidden rounded-xl border border-dashed border-white/25 bg-white/5 transition-colors hover:border-[var(--red)]/60">
              {previewArtwork ? (
                <>
                  <img
                    src={previewArtwork}
                    alt="artwork"
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
                    <span className="flex flex-col items-center gap-1 text-white">
                      <RiImageAddLine className="text-2xl" />
                      <span className="text-xs">Change</span>
                    </span>
                  </div>
                </>
              ) : (
                <span className="flex flex-col items-center gap-2 text-white/50">
                  <RiImageAddLine className="text-3xl" />
                  <span className="text-xs">Add artwork</span>
                </span>
              )}
              <input
                ref={upload}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                name="attachment"
                className="absolute inset-0 z-30 cursor-pointer opacity-0"
              />
            </label>
          </div>

          {/* fields */}
          <div className="w-full flex-1">
            <span className="text-[15px] text-white">Name</span>
            <AntDInputField
              name="playlistName"
              value={currentPlaylist && currentPlaylist?.title}
              rules={[
                {
                  required: true,
                  message: "Please enter playlist name!",
                },
              ]}
            />

            <span className="text-[15px] text-white">Description</span>
            <AntDInputField
              name="description"
              value={currentPlaylist && currentPlaylist?.description}
            />
          </div>
        </div>

        {/* footer */}
        <div className="flex items-center justify-between gap-4 max-sm:flex-col max-sm:items-stretch">
          <div className="flex items-center gap-3">
            <p className="text-md text-white">Make Public</p>
            <Switch
              defaultChecked={makePublic}
              onChange={onChange}
              style={{ backgroundColor: makePublic ? "red" : "#313131" }}
            />
          </div>

          <button
            className="button-red min-w-[160px] rounded-full px-4 py-3 text-base font-bold text-white transition-opacity hover:opacity-90 max-sm:w-full"
            type="submit"
          >
            {loadingSpin ? (
              <LoadingSpin fontSize={30} color={"white"} />
            ) : currentPlaylist ? (
              `Update Playlist`
            ) : (
              `Create Playlist`
            )}
          </button>
        </div>
      </Form>
    </div>
  );
};

export default CreatePlayList;